import type { FC } from "react";
import { useMemo } from "react";
import { Card } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import type { GetThemeValueForKey } from "tamagui";
import { Text, XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import { OverviewCard, OverviewEmptyCard } from "./overview-common";

export const OverviewWellbeing: FC = () => {
  const { t } = useTranslation("overview");
  const [data] = api.overview.wellbeing.useSuspenseQuery();
  const empty = useMemo(() => data === null, [data]);

  return (
    <OverviewCard
      title={t("wellbeing.title")}
      subtitle={t("wellbeing.subtitle")}
    >
      <XStack width="100%" flex={1} gap={"$2"} alignItems="center">
        <WellbeingCard
          unit="level"
          value={data?.avg}
          title={t("wellbeing.avg")}
          empty={empty}
        />
        <WellbeingCard
          unit="percent"
          variant="critical"
          value={data?.critical}
          title={t("wellbeing.critical")}
          empty={empty}
        />
        <WellbeingCard
          unit="percent"
          variant="middle"
          value={data?.middle}
          title={t("wellbeing.middle")}
          empty={empty}
        />
        <WellbeingCard
          unit="percent"
          variant="resource"
          value={data?.resource}
          title={t("wellbeing.resource")}
          empty={empty}
        />
      </XStack>
      <OverviewEmptyCard text={t("wellbeing.empty")} visible={empty} />
    </OverviewCard>
  );
};

interface WellbeingCardProps {
  title: string;
  value: number | undefined;
  unit: "level" | "percent";
  variant?: "critical" | "middle" | "resource";
  empty?: boolean;
}

const WellbeingCard: FC<WellbeingCardProps> = ({
  title,
  value = 0,
  variant,
  unit,
  empty = false,
}) => {
  const { color, bgColor, titleColor } = useMemo<{
    color: GetThemeValueForKey<"color">;
    bgColor: GetThemeValueForKey<"backgroundColor">;
    titleColor?: GetThemeValueForKey<"color">;
  }>(() => {
    if (empty)
      return {
        color: "$gray-200",
        bgColor: "$gray-100",
        titleColor: "$gray-200",
      };

    switch (variant) {
      case "critical":
        return { color: "$darkpink-500", bgColor: "#FEE3E3" };
      case "middle":
        return { color: "$score-3", bgColor: "#FFEED6" };
      case "resource":
        return { color: "$lightgreen-500", bgColor: "#D4FFE2" };
      default:
        return { color: "$color", bgColor: "$gray-100" };
    }
  }, [empty, variant]);

  return (
    <Card
      flex={1}
      flexBasis={1}
      shadow="md"
      alignItems="center"
      paddingVertical={"$3"}
      paddingHorizontal={"$4"}
      borderWidth={"$0.25"}
      borderColor={"$gray-600"}
    >
      <YStack gap={"$2"}>
        <Card
          variant="gray"
          alignItems="center"
          borderRadius={"$5"}
          paddingVertical={"$1"}
          paddingHorizontal={"$4"}
          backgroundColor={bgColor}
        >
          {unit === "level" ? (
            <XStack>
              <Text
                fontFamily={"$body"}
                fontSize={"$7"}
                fontWeight={"$6"}
                color={color}
              >
                {value}
              </Text>
              <Text
                fontFamily={"$body"}
                fontSize={"$7"}
                color={empty ? "$gray-200" : "$gray-secondary-400"}
              >
                /100
              </Text>
            </XStack>
          ) : (
            <Text fontFamily={"$body"} fontSize={"$7"} color={color}>
              {value}%
            </Text>
          )}
        </Card>
        <Text
          fontFamily={"$body"}
          fontWeight={500}
          fontSize={"$4"}
          color={titleColor}
        >
          {title}
        </Text>
      </YStack>
    </Card>
  );
};
